import { useSelector } from "react-redux";
import _ from "lodash";

const usePm2 = () => {
  const { pm2Status, pm2Loading, pm2Error, pm2Restarting, lastRestartResult, lastUpdated, pm2Logs, pm2LogsLoading } =
    useSelector((store) => store.pm2Reducer);

  // Compute derived data
  const hasData = !_.isEmpty(pm2Status);
  const hasError = !_.isEmpty(pm2Error);
  const isManaged = pm2Status?.managed || false;

  // Process status data
  const processName = pm2Status?.name || "N/A";
  const processStatus = pm2Status?.status || "unknown";
  const processPid = pm2Status?.pid || null;
  const restartCount = pm2Status?.restarts || 0;

  // Memory and CPU
  const memoryBytes = pm2Status?.memory || 0;
  const memoryMB = (memoryBytes / (1024 * 1024)).toFixed(2);
  const cpuPercent = pm2Status?.cpu?.toFixed(2) || 0;

  // Uptime calculation
  const uptimeMs = pm2Status?.uptime
    ? new Date().getTime() - pm2Status.uptime
    : 0;

  const formatUptime = (ms) => {
    if (!ms) return "N/A";
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ${hours % 24}h ${minutes % 60}m`;
    if (hours > 0) return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  const uptimeFormatted = formatUptime(uptimeMs);

  // Status color coding
  const getStatusColor = () => {
    const statusColors = {
      online: "green",
      stopped: "red",
      errored: "red",
      stopping: "orange",
      launching: "blue",
      unknown: "gray",
    };
    return statusColors[processStatus] || "gray";
  };

  const statusColor = getStatusColor();

  // Check if process is healthy
  const isHealthy = processStatus === "online" && isManaged;

  // Memory usage percentage (assuming 512MB max for visualization)
  const maxMemoryMB = 512;
  const memoryPercent = Math.min((memoryMB / maxMemoryMB) * 100, 100);

  // Parse PM2 logs from array format
  const parsedLogs = (() => {
    if (!pm2Logs || !pm2Logs.logs || !Array.isArray(pm2Logs.logs)) {
      return { out: "", err: "" };
    }

    // Combine all log messages into a single string
    const allLogs = pm2Logs.logs
      .map(log => {
        const timestamp = log.timestamp ? `[${log.timestamp}] ` : "";
        const process = log.process ? `${log.process}: ` : "";
        return `${timestamp}${process}${log.message}`;
      })
      .join("\n");

    return { out: allLogs, err: "" };
  })();

  const hasLogs = !_.isEmpty(pm2Logs) && pm2Logs.logs && pm2Logs.logs.length > 0;

  return {
    // Raw data
    pm2Status,
    pm2Loading,
    pm2Error,
    pm2Restarting,
    lastRestartResult,
    lastUpdated,
    pm2Logs: parsedLogs, // Use parsed logs instead of raw
    pm2LogsLoading,

    // Computed booleans
    hasData,
    hasError,
    isManaged,
    isHealthy,
    hasLogs,

    // Process info
    processName,
    processStatus,
    processPid,
    restartCount,
    statusColor,

    // Metrics
    memoryBytes,
    memoryMB,
    memoryPercent,
    cpuPercent,
    uptimeMs,
    uptimeFormatted,
  };
};

export default usePm2;
